import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_COLUMN_WIDTH_PERCENTAGE,
  ESTIMATED_HEADER_HEIGHT,
  MIN_COLUMN_SIZE,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  ROW_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-rep-view-table/prospects-rep-view-table-columns';
import { SEARCHBAR_MAX_WIDTH } from '@/experiences/sales-portal/domain/prospects/screens/prospects/constants';
import { Skeleton } from '@usepepper/sprout-kit-ui';
import { View, ViewStyle } from 'react-native';

const SEARCH_BAR_HEIGHT = 40;
const SKELETON_ROW_COUNT = 8;
const DEFAULT_COLUMN_COUNT = 3;
const AVATAR_SIZE = 32;
const HEADER_TEXT_HEIGHT = 12;
const HEADER_TEXT_WIDTH = 72;
const NAME_CELL_HEIGHT = 14;
const NAME_CELL_WIDTH = 140;
const NAME_CELL_DESCRIPTION_HEIGHT = 12;
const NAME_CELL_DESCRIPTION_WIDTH = 200;
const CELL_TEXT_HEIGHT = 14;
const CELL_TEXT_WIDTH = 80;

const getRowFadeStyle = (index: number): ViewStyle => ({
  opacity: 1 - index * 0.1,
});

export const ProspectsRepViewSkeleton: React.FC = () => {
  const [styles, theme] = useStyles();

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.prospects.loading')}
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      style={styles.container}
    >
      <View style={styles.searchBar}>
        <Skeleton
          borderRadius={theme.sk.radii.radiusMedium}
          height={SEARCH_BAR_HEIGHT}
          loadingLabel=""
          width="100%"
        />
      </View>

      <View style={styles.tableContainer}>
        <View style={styles.headerRow}>
          <View style={[styles.headerCell, styles.nameColumn]}>
            <Skeleton
              borderRadius={theme.sk.radii.radiusMedium}
              height={HEADER_TEXT_HEIGHT}
              loadingLabel=""
              width={HEADER_TEXT_WIDTH}
            />
          </View>
          {Array.from({ length: DEFAULT_COLUMN_COUNT }).map(
            (_, columnIndex) => (
              <View
                key={columnIndex}
                style={[styles.headerCell, styles.defaultColumn]}
              >
                <Skeleton
                  borderRadius={theme.sk.radii.radiusMedium}
                  height={HEADER_TEXT_HEIGHT}
                  loadingLabel=""
                  width={HEADER_TEXT_WIDTH}
                />
              </View>
            ),
          )}
        </View>

        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
          <View key={rowIndex} style={getRowFadeStyle(rowIndex)}>
            <View style={styles.bodyRow}>
              <View style={[styles.bodyCell, styles.nameColumn]}>
                <View style={styles.nameCell}>
                  <Skeleton
                    borderRadius={AVATAR_SIZE / 2}
                    height={AVATAR_SIZE}
                    loadingLabel=""
                    width={AVATAR_SIZE}
                  />
                  <View style={styles.nameText}>
                    <Skeleton
                      borderRadius={theme.sk.radii.radiusMedium}
                      height={NAME_CELL_HEIGHT}
                      loadingLabel=""
                      width={NAME_CELL_WIDTH}
                    />
                    <Skeleton
                      borderRadius={theme.sk.radii.radiusMedium}
                      height={NAME_CELL_DESCRIPTION_HEIGHT}
                      loadingLabel=""
                      width={NAME_CELL_DESCRIPTION_WIDTH}
                    />
                  </View>
                </View>
              </View>
              {Array.from({ length: DEFAULT_COLUMN_COUNT }).map(
                (_, columnIndex) => (
                  <View
                    key={columnIndex}
                    style={[styles.bodyCell, styles.defaultColumn]}
                  >
                    <Skeleton
                      borderRadius={theme.sk.radii.radiusMedium}
                      height={CELL_TEXT_HEIGHT}
                      loadingLabel=""
                      width={CELL_TEXT_WIDTH}
                    />
                  </View>
                ),
              )}
            </View>
            <View style={styles.divider} />
          </View>
        ))}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  bodyCell: {
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  bodyRow: {
    flexDirection: 'row',
    minHeight: ROW_HEIGHT,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceSMedium,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  defaultColumn: {
    flex: DEFAULT_COLUMN_WIDTH_PERCENTAGE,
    minWidth: MIN_COLUMN_SIZE,
  },
  divider: {
    backgroundColor: theme.sk.color.divider.default,
    height: 1,
  },
  headerCell: {
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  headerRow: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
    flexDirection: 'row',
    minHeight: ESTIMATED_HEADER_HEIGHT,
  },
  nameCell: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
  },
  nameColumn: {
    flex: NAME_COLUMN_PERCENTAGE,
    minWidth: MIN_NAME_COLUMN_SIZE,
  },
  nameText: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  searchBar: {
    alignSelf: 'center',
    maxWidth: SEARCHBAR_MAX_WIDTH,
    width: '100%',
  },
  tableContainer: {
    flex: 1,
  },
}));
